import './styles/ContractSummary_W.scss';

import { useClassHelper } from '../../../../hooks';
import { WebsheetTemplateProps } from '../../../../types';
import { Stack } from '../../../Stack';
import { TermsAndConditionsBase } from '../../components/TermsAndConditionsBase';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useLocalisation } from '../../hooks/useLocalisation';
import { resolveContractContent } from './helpers';

const componentName = 'c-ContractSummary_W';

interface Props extends WebsheetTemplateProps {
  onClose: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  // 'linked' (sa-watch / Family Plan) mode prefers the `family_plan_contract_*`
  // wizard twins, falling back to the base `contract_*` keys.
  isLinked?: boolean;
  'data-dd-action-name-close'?: string;
  'data-dd-action-name-submit'?: string;
}

export const ContractSummaryW = (props: Props) => {
  const c = useClassHelper(componentName);
  const {
    values,
    currentLocale,
    text,
    onClose,
    onSubmit,
    isLinked,
    'data-dd-action-name-close': dataDdActionNameClose,
    'data-dd-action-name-submit': dataDdActionNameSubmit,
  } = props;
  const getStyles = useGetStyles({ values });
  const { localisations } = values;

  const localisation = useLocalisation(localisations, currentLocale);
  const source = localisation?.['contract'] || text;

  // Family-first resolution (same chain as the terms/transfer/error copy):
  // linked mode prefers the `family_plan_` twin, falling back to the base
  // key — paired (or missing) mode reads the base keys only, so existing
  // configs are unchanged.
  const pick = (key: string) => {
    return (
      (isLinked ? source?.[`family_plan_${key}`] : undefined) || source?.[key]
    );
  };

  const contractTitle = pick('contract_title');
  const contractSubtitle = pick('contract_subtitle');
  // The body alone decides whether the page exists, so it resolves through
  // the same content-aware fallback as the Watch_A submit gate.
  const contractContent = resolveContractContent(Boolean(isLinked), source);
  const contractBack = pick('contract_back');
  const contractContinue = pick('contract_continue');

  return (
    <TermsAndConditionsBase
      getStyles={getStyles}
      styling={{
        title: {
          text: contractTitle || 'Contract Summary',
          fontFamily: values?.fontTitle,
        },
        subtitle: {
          text:
            contractSubtitle || 'Please review the summary of your contract',
          fontFamily: values?.fontTitle,
        },
        content: {
          text: contractContent || [],
          valueFontParagraph: values?.fontParagraph,
        },
      }}
      footer={
        <Stack direction="row" justify="between" className={c('__footer')}>
          <button
            type="button"
            className={c('__highlight')}
            onClick={onClose}
            style={{
              color: values?.colorLink,
            }}
            data-dd-action-name={dataDdActionNameClose}
          >
            {contractBack || 'Back'}
          </button>
          <button
            type="button"
            className={c('__highlight')}
            onClick={onSubmit}
            style={{
              color: values?.colorLink,
            }}
            data-dd-action-name={dataDdActionNameSubmit}
          >
            {contractContinue || 'Continue'}
          </button>
        </Stack>
      }
    />
  );
};
